import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import "./Toast.css";
import useGetDevice from "./useGetDevice";

const SnackbarContext = createContext();

export function useSnackbar() {
  return useContext(SnackbarContext);
}

// Toast-like Snackbar component for desktop
function SnackbarToast({ severity = "info", message, onClose, persistent = false }) {
  const [phase, setPhase] = useState("enter");
  const timerRef = useRef(null);

  // Map MUI severity to Toast variant
  const variantMap = {
    success: "success",
    error: "error",
    warning: "warning",
    info: "info",
  };
  const variant = variantMap[severity] || "neutral";

  useEffect(() => {
    const id = setTimeout(() => setPhase("idle"), 300);
    return () => clearTimeout(id);
  }, []);

  // Auto close after 3 seconds unless persistent
  useEffect(() => {
    if (persistent) return;
    const autoCloseAfter = 3000;
    const id = setTimeout(() => {
      if (typeof onClose === "function") {
        setPhase("exit");
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          onClose();
        }, 180);
      }
    }, autoCloseAfter);
    return () => clearTimeout(id);
  }, [persistent, onClose]);

  const handleClose = () => {
    setPhase("exit");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onClose && onClose();
    }, 180);
  };

  const cls = `tl-toast tl-toast--${variant} ${phase}`;

  return (
    <div className={cls} role="status" aria-live="polite">
      <div className="tl-toast__bar" />
      <div className="tl-toast__content" style={{ width: "100%" }}>
        <div className="tl-toast__message">{message}</div>
        <div className="tl-toast__spacer" />
        {onClose && (
          <button
            className="tl-toast__close"
            onClick={handleClose}
            type="button"
            aria-label="Close"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
}

// Toast container — on the POS cart screen it slots between the island and the
// right-side icons instead of overlapping the top-right corner.
function SnackbarToastContainer({ children, anchorOrigin, posOverride }) {
  const { vertical, horizontal } = anchorOrigin;

  const positionStyles = posOverride || {
    position: "fixed",
    zIndex: 9999,
    pointerEvents: "none",
    ...(vertical === "bottom" && { bottom: "24px" }),
    ...(vertical === "top" && { top: "24px" }),
    ...(horizontal === "center" && {
      left: "50%",
      transform: "translateX(-50%)",
    }),
    ...(horizontal === "left" && { left: "24px" }),
    ...(horizontal === "right" && { right: "24px" }),
  };

  return (
    <div style={positionStyles}>
      <div style={{ pointerEvents: "auto" }}>{children}</div>
    </div>
  );
}

const POS_PATH = "/grocery/cart";

export function SnackbarProvider({ children }) {
  const isMobileDevice = useGetDevice();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("info");
  const [subMessage, setSubMessage] = useState("");
  const [primaryButtonProps, setPrimaryButtonProps] = useState(null);
  const [secondaryButtonProps, setSecondaryButtonProps] = useState(null);
  const [onPrimaryClick, setOnPrimaryClick] = useState(null);
  const [onSecondaryClick, setOnSecondaryClick] = useState(null);
  const [vertical, horizontal] = isMobileDevice ? ["top", "center"] : ["top", "right"];
  const [persistent, setPersistent] = useState(false);

  const handleClose = (event, reason) => {
    if (reason === "clickaway" || persistent) {
      return;
    }
    setOpen(false);
  };

  const showSnackbar = (
    message = "",
    severity = "info",
    subMessage = "",
    primaryButtonProps = null,
    secondaryButtonProps = null,
    onPrimaryClick = null,
    onSecondaryClick = null,
    persistent = false
  ) => {
    setOpen(true);
    setSeverity(severity);
    setPersistent(persistent);

    if (isMobileDevice) {
      setMessage(message);
      setSubMessage(subMessage);
      setPrimaryButtonProps(primaryButtonProps);
      setSecondaryButtonProps(secondaryButtonProps);
      setOnPrimaryClick(() => onPrimaryClick || handleClose);
      setOnSecondaryClick(() => onSecondaryClick || handleClose);
    } else {
      setMessage(message);
    }
  };

  // On the POS cart screen (desktop only): slide the toast into the gap
  // between the notification island and the right-side icon cluster,
  // rather than overlapping the top-right icons.
  const isPOS =
    !isMobileDevice &&
    typeof window !== "undefined" &&
    window.location.pathname.includes(POS_PATH);

  const posOverride = isPOS
    ? {
        position: "fixed",
        zIndex: 9999,
        pointerEvents: "none",
        top: "6px",
        left: "calc(50% + 146px)",
        maxWidth: "calc(50% - 162px)",
      }
    : null;

  return (
    <SnackbarContext.Provider value={showSnackbar}>
      {children}
      {!isMobileDevice && open && (
        <SnackbarToastContainer
          anchorOrigin={{ vertical, horizontal }}
          posOverride={posOverride}
        >
          <SnackbarToast
            severity={severity}
            message={message}
            onClose={handleClose}
            persistent={persistent}
          />
        </SnackbarToastContainer>
      )}
    </SnackbarContext.Provider>
  );
}
